import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/store';
import { Button, Grid, CircularProgress, Stack, FormLabel, FormControlLabel, Checkbox, Tooltip, Typography } from '@mui/material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';

import { gotoNextMenu, gotoPreviousMenu } from '../../../store/assessment.slice';
import { RootState } from '../../../reducers';
import MemberCareCircle from '../../Members/MemberDetail/MemberCareCircle';
import AlertDialog from '../../AlertDialog/AlertDialog';
import { saveEngagementDraft, setEngagementDraft } from '../../../store/documentEngagement.slice';
import { StatusEnum } from '../../../Enum/StatusEnum';
import { copyObject } from '../../../utils/common';
import { SummaryNoteTypeEnum } from '../../../Enum/SummaryNoteTypeEnum';
import { roleEnum } from '../../../utils/assessments';
import { setShowMessage } from '../../../store/shared.slice';
import Authorization from '../../../utils/Authorization';
import InfoIcon from '@mui/icons-material/Info';
import HelpPage from '../../../assets/documents/Assessment_Vital_Signs_Care_Circle_Demographics_PCP.pdf';

const MemberAssessmentCareCircle = () => {
    const authData = new Authorization();
    const [showSaveAlert, setShowSaveAlert] = useState<boolean>(false);
    const dispatch = useAppDispatch();
    const { engagementDraft, memberAssessmentHistory, isSaving } = useSelector((state: RootState) => state.documentEngagement);
    const isOnboarding = engagementDraft.AssessedRole?.RoleName === roleEnum.FIELD_ONBOARDING;

    const handleCareCircleConfirmationChange = (checked: boolean) => {
        // Update local state first so the checkbox always toggles, then persist.
        // A failed draft save (e.g. incomplete Heidi draft) must not block confirmation UI.
        const updatedEngagementDraft = {
            ...engagementDraft,
            CareCircleConfirmation: {
                ...engagementDraft.CareCircleConfirmation,
                AskedAndConfirmed: checked,
                CreatedBy: { Id: authData.UserId, Name: authData.UserName },
                CreatedDate: new Date().toISOString(),
            },
        };
        dispatch(setEngagementDraft(updatedEngagementDraft));
        const updatedMemberAssessmentHistory = { ...memberAssessmentHistory, EngagementDraft: updatedEngagementDraft };
        dispatch(saveEngagementDraft(updatedMemberAssessmentHistory, false));
    };

    const handleSave = () => {
        if (isOnboarding && !engagementDraft.CareCircleConfirmation?.AskedAndConfirmed) {
            dispatch(setShowMessage(true, 'Please confirm Care Circle has been asked to proceed.', 'error'));
        } else {
            setShowSaveAlert(true);
        }
    };

    const handleOnConfirmToNext = (isWithoutComplete = false) => {
        if (isWithoutComplete) {
            let data = copyObject(engagementDraft);
            data.SummaryNotes = data.SummaryNotes?.filter((s) => s.Type !== SummaryNoteTypeEnum.CareCircle) || [];
            data.SummaryNotes.push({
                Id: '',
                CptCode: '',
                Zcode: '',
                Note: 'Completed Care Circle Information',
                Type: SummaryNoteTypeEnum.CareCircle,
                IsConfirm: true,
                OrderBy: 34,
                DisplayName: 'Care Circle',
            });
            data.CareCircleConfirmation.CareCircleStatus = StatusEnum.Completed;
            const updatedMemberAssessmentStatuses = {
                ...memberAssessmentHistory.MemberAssessmentStatuses,
                CareCircleStatus: StatusEnum.Completed,
            };
            const updatedMemberAssessmentHistory = {
                ...memberAssessmentHistory,
                MemberAssessmentStatuses: updatedMemberAssessmentStatuses,
                EngagementDraft: data,
            };
            dispatch(saveEngagementDraft(updatedMemberAssessmentHistory, true));
        } else {
            dispatch(gotoNextMenu());
        }
    };

    return (
        <>
            <Stack direction={'row'} alignItems={'center'} spacing={1} sx={{ ml: 4 }}>
                <Typography variant="h6">Care Circle</Typography>
                <Tooltip title="Care Circle Help Document">
                    <InfoIcon color="primary" fontSize="small" cursor="pointer" onClick={() => window.open(`${HelpPage}`, '_blank')} />
                </Tooltip>
            </Stack>
            <Grid container direction="column" spacing={2} className="content-panel">
                <Grid className="content-form">
                    <MemberCareCircle />
                </Grid>
                <Grid size={{ sm: 12, md: 12, lg: 12 }}>
                    <hr className="hr-lite" />
                </Grid>
                <Grid size={12}>
                    <Stack direction={'row'} spacing={2} alignItems={'center'} sx={{ paddingX: 2, mb: 2 }}>
                        <FormLabel required={isOnboarding}>Care Circle asked and confirmed</FormLabel>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={engagementDraft?.CareCircleConfirmation?.AskedAndConfirmed || false}
                                    onChange={(e) => handleCareCircleConfirmationChange(e.target.checked)}
                                />
                            }
                            label={''}
                        />
                    </Stack>
                </Grid>
                <Grid justifyContent="flex-end" className="content-footer">
                    <Button
                        variant="contained"
                        color="secondary"
                        sx={{ marginRight: 2 }}
                        disabled={isSaving}
                        onClick={() => dispatch(gotoPreviousMenu())}
                        startIcon={<ArrowBackIosIcon />}
                    >
                        Previous
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        onClick={handleSave}
                        disabled={isSaving}
                        endIcon={isSaving ? <CircularProgress size={18} color="inherit" /> : <ArrowForwardIosIcon />}
                    >
                        Next
                    </Button>
                </Grid>
            </Grid>
            <AlertDialog
                open={showSaveAlert}
                title="Care Circle"
                cancelText="Go Back"
                okText="Confirm"
                onConfirm={() => handleOnConfirmToNext(true)}
                onSkip={() => handleOnConfirmToNext()}
                onClose={() => setShowSaveAlert(false)}
                isLoading={isSaving}
                message="I confirm and acknowledge that Care Circle is completed."
            />
        </>
    );
};

export default MemberAssessmentCareCircle;
